import { get, writable } from "svelte/store";
import { getDistance } from "geolib";
import { millisecondsFromHours } from "../helpers/time.js";
import persisted from "./common/persisted.js";
import journey, { journeySubscribeThrottled } from "./journey.js";
import { photos as photoAt } from "../api/api.js";
import windows from "./windows.js";

const PHOTO_MAXIMUM_AGE_HOURS = 72;
const MINIMUM_INTERVAL_METERS = 1250;

function loadImage(url) {
    return new Promise((resolve) => {
        const image = new Image();
        image.onload = resolve;
        image.src = url;
    });
}

function recentPhotos(photos, maximumAgeHours = PHOTO_MAXIMUM_AGE_HOURS) {
    const limit = Date.now() - millisecondsFromHours(maximumAgeHours);
    return photos.filter((photo) => photo.createdAt > limit);
}

function createPhotosStore() {
    const store = writable([]);
    const { subscribe, update, set } = store;

    let lastUpdatedAt;

    function updatePhotos(location) {
        lastUpdatedAt = location;

        photoAt(location)
            .then(({ photo }) => {
                if (!photo) {
                    return;
                }

                loadImage(photo).then(() => {
                    update((state) => {
                        const existing = recentPhotos(state);

                        const isUnique = existing.every(
                            ({ url }) => url != photo
                        );

                        if (!isUnique) {
                            return state;
                        }

                        return existing.concat({
                            url: photo,
                            createdAt: Date.now()
                        });
                    });
                });
            })
            .catch((error) => {
                console.warn("failed to take photo", error.message);
            });
    }

    journeySubscribeThrottled(10000, ({ location }) => {
        const shouldUpdate =
            location &&
            (lastUpdatedAt === undefined ||
                getDistance(lastUpdatedAt, location) >
                    MINIMUM_INTERVAL_METERS) &&
            windows.isOpen("photos");

        if (!shouldUpdate) {
            return;
        }

        updatePhotos(location);
    });

    windows.onOpen("photos", function didOpenWindow() {
        const shouldUpdate = get(store).length === 0;

        if (shouldUpdate) {
            updatePhotos(get(journey).location);
        }
    });

    return {
        subscribe,

        set(photos) {
            set(recentPhotos(photos));
        }
    };
}

const photos = persisted("photos:v5", true, createPhotosStore());

export default photos;
